/* Jhilke Studio: photo-app gestures for the fullscreen product gallery on phones (under 750px).
   - one finger, fitted photo: swipe left/right to change photo (follows the finger, then snaps);
   - one finger, fitted photo: swipe down to close (follows the finger, closes past 80px or on a flick);
   - zoomed photo: this script steps aside, the theme's drag-zoom-wrapper pans (double-tap and pinch live there);
   - a "2 / 5" counter, announced politely to screen readers.
   Desktop is untouched (jhilke-zoom-fit.js). The close button, Esc and the thumbnail strip keep working.
   HA 2026-10-12: the touchmove listener that cancels the browser's own gesture sits on the fullscreen photo list
   only. It used to sit on the whole document (passive: false), so every touch scroll on a product page, the page
   and the photo carousel too, waited for the main thread before it could move. The theme is told which photo is
   showing only once the glide to it has finished; it used to be told during the glide, and its instant scroll then
   cut the glide short (the photo jumped). A swipe that starts during a glide counts from the photo it is gliding to. */
(function () {
  'use strict';
  try {
    var MOBILE = window.matchMedia('(max-width: 749px)');
    var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
    var LOCK_PX = 8; // movement before we decide horizontal or vertical
    var CLOSE_PX = 80; // swipe-down distance that closes
    var FLICK_PX_MS = 0.6; // fast flick down closes below CLOSE_PX
    var PAGE_FRAC = 0.2; // horizontal drag that turns the page
    var PAGE_FLICK_PX_MS = 0.4;
    var ZOOMED = 1.02; // wrapper scale above which we do not touch the gesture
    var LIST = '.dialog-zoomed-gallery';

    var SETTLE_MAX_MS = 900; // a glide that never reports its end is treated as done after this
    var gesture = null;
    var sync = new WeakMap(); // list -> last index pushed to the theme (thumbnail + high-res image)
    var gliding = new WeakMap(); // list -> { to: index, timer } while the glide to a photo runs

    function indexOf(list) {
      var w = list.clientWidth || 1;
      var n = list.children.length;
      return Math.max(0, Math.min(n - 1, Math.round(list.scrollLeft / w)));
    }

    function scaleAt(list, index) {
      var li = list.children[index];
      var wrap = li && li.querySelector('drag-zoom-wrapper');
      if (!wrap) return 1;
      return parseFloat(wrap.style.getPropertyValue('--drag-zoom-scale')) || 1;
    }

    function counterFor(dialog) {
      var c = dialog.querySelector('.jh-gallery-count');
      if (c) return c;
      c = document.createElement('div');
      c.className = 'jh-gallery-count';
      c.setAttribute('role', 'status');
      c.setAttribute('aria-live', 'polite');
      c.setAttribute('aria-atomic', 'true');
      c.innerHTML = '<span aria-hidden="true" data-jh-count-visual></span><span class="visually-hidden" data-jh-count-text></span>';
      dialog.appendChild(c);
      return c;
    }

    function updateCounter(dialog, list) {
      var n = list.children.length;
      if (n < 2) return;
      var c = counterFor(dialog);
      var i = indexOf(list) + 1;
      var visual = c.querySelector('[data-jh-count-visual]');
      var text = c.querySelector('[data-jh-count-text]');
      var vt = i + ' / ' + n;
      if (visual.textContent !== vt) {
        visual.textContent = vt;
        text.textContent = 'Photo ' + i + ' of ' + n;
      }
    }

    // Tell the theme which photo is showing (thumbnail highlight, sharp image). Its own scroll hook listens on the
    // dialog, which does not scroll on phones, so we do it once the swipe has settled.
    function syncTheme(dialog, list, index) {
      var i = typeof index === 'number' ? index : indexOf(list);
      if (sync.get(list) === i) return;
      sync.set(list, i);
      var zd = dialog.closest('zoom-dialog');
      if (zd && typeof zd.selectThumbnail === 'function') {
        try { zd.selectThumbnail(i, { behavior: 'instant' }); } catch (e) { /* ignore */ }
      }
    }

    function resetStyles(dialog, list) {
      list.style.removeProperty('transform');
      list.style.removeProperty('transition');
      list.style.removeProperty('scroll-snap-type');
      list.style.removeProperty('scroll-behavior');
      dialog.style.removeProperty('opacity');
      dialog.style.removeProperty('transition');
    }

    function closeDialog(dialog) {
      var zd = dialog.closest('zoom-dialog');
      if (zd && typeof zd.closeDialog === 'function') zd.closeDialog();
      else if (dialog.open) dialog.close();
    }

    function onStart(e) {
      gesture = null;
      if (!MOBILE.matches) return;
      var t = e.target;
      if (!t || !t.closest) return;
      var list = t.closest(LIST);
      if (!list) return;
      var dialog = list.closest('dialog');
      if (!dialog || !dialog.open) return;
      wire(dialog);
      if (e.touches.length !== 1) return; // pinch: the theme handles it
      var glide = gliding.get(list);
      var idx = glide ? glide.to : indexOf(list); // mid-glide: count from the photo it is heading to
      if (scaleAt(list, idx) > ZOOMED) return; // zoomed: one finger pans the photo
      var p = e.touches[0];
      gesture = {
        list: list, dialog: dialog, idx: idx, x: p.clientX, y: p.clientY, left: list.scrollLeft,
        axis: '', t0: performance.now(), lx: p.clientX, ly: p.clientY, lt: performance.now(), vx: 0, vy: 0, dx: 0, dy: 0,
      };
    }

    function abort(g) {
      // second finger or zoom began: put everything back where it was
      if (g.axis === 'x') settle(g, g.idx);
      else if (g.axis === 'y') snapBack(g);
      gesture = null;
    }

    function onMove(e) {
      var g = gesture;
      if (!g) return;
      if (e.touches.length !== 1 || scaleAt(g.list, g.idx) > ZOOMED) return abort(g);
      var p = e.touches[0];
      var now = performance.now();
      var dt = now - g.lt;
      if (dt > 0) {
        g.vx = (p.clientX - g.lx) / dt;
        g.vy = (p.clientY - g.ly) / dt;
      }
      g.lx = p.clientX; g.ly = p.clientY; g.lt = now;
      g.dx = p.clientX - g.x;
      g.dy = p.clientY - g.y;

      if (!g.axis) {
        if (Math.max(Math.abs(g.dx), Math.abs(g.dy)) < LOCK_PX) return;
        g.axis = Math.abs(g.dx) > Math.abs(g.dy) ? 'x' : 'y';
        if (g.axis === 'x') {
          endGlide(g.list); // the finger takes over from a glide still running
          g.list.style.setProperty('scroll-snap-type', 'none');
          g.list.style.setProperty('scroll-behavior', 'auto');
        } else {
          g.list.style.setProperty('transition', 'none');
          g.dialog.style.setProperty('transition', 'none');
        }
      }
      if (e.cancelable) e.preventDefault();

      if (g.axis === 'x') {
        g.list.scrollLeft = g.left - g.dx;
      } else {
        var dy = Math.max(0, g.dy); // only downward moves the photo
        g.list.style.setProperty('transform', 'translate3d(0,' + dy + 'px,0)');
        g.dialog.style.setProperty('opacity', String(1 - Math.min(0.75, dy / (window.innerHeight * 0.8))));
      }
    }

    function endGlide(list) {
      var glide = gliding.get(list);
      if (!glide) return null;
      window.clearTimeout(glide.timer);
      gliding.delete(list);
      return glide;
    }

    // The glide has reached its photo (or ran out of time): now the theme may update the thumbnail and image.
    function finishGlide(dialog, list) {
      var glide = endGlide(list);
      if (glide) syncTheme(dialog, list, glide.to);
    }

    function settle(g, target) {
      var list = g.list;
      var n = list.children.length;
      target = Math.max(0, Math.min(n - 1, target));
      list.style.removeProperty('scroll-behavior');
      list.style.removeProperty('scroll-snap-type');
      endGlide(list);
      var left = target * list.clientWidth;
      if (REDUCED.matches || Math.abs(list.scrollLeft - left) < 1) {
        list.scrollTo({ left: left, behavior: 'instant' });
        syncTheme(g.dialog, list, target);
        return;
      }
      gliding.set(list, {
        to: target,
        timer: window.setTimeout(function () { finishGlide(g.dialog, list); }, SETTLE_MAX_MS),
      });
      list.scrollTo({ left: left, behavior: 'smooth' });
    }

    function snapBack(g) {
      if (REDUCED.matches) return resetStyles(g.dialog, g.list);
      g.list.style.setProperty('transition', 'transform 180ms ease-out');
      g.dialog.style.setProperty('transition', 'opacity 180ms ease-out');
      g.list.style.setProperty('transform', 'translate3d(0,0,0)');
      g.dialog.style.setProperty('opacity', '1');
      window.setTimeout(function () { resetStyles(g.dialog, g.list); }, 220);
    }

    function onEnd(e) {
      var g = gesture;
      if (!g) return;
      if (e.touches.length > 0) return;
      gesture = null;
      if (g.axis === 'x') {
        var w = g.list.clientWidth || 1;
        var target = g.idx;
        var farEnough = Math.abs(g.dx) > w * PAGE_FRAC;
        var flicked = Math.abs(g.vx) > PAGE_FLICK_PX_MS && g.vx * g.dx > 0;
        if (farEnough || flicked) target = g.idx + (g.dx < 0 ? 1 : -1);
        settle(g, target);
      } else if (g.axis === 'y') {
        if (e.type === 'touchend' && (g.dy > CLOSE_PX || (g.dy > 24 && g.vy > FLICK_PX_MS))) {
          if (REDUCED.matches) {
            resetStyles(g.dialog, g.list);
            closeDialog(g.dialog);
            return;
          }
          g.list.style.setProperty('transition', 'transform 160ms ease-in');
          g.dialog.style.setProperty('transition', 'opacity 160ms ease-in');
          g.list.style.setProperty('transform', 'translate3d(0,' + window.innerHeight + 'px,0)');
          g.dialog.style.setProperty('opacity', '0');
          window.setTimeout(function () {
            closeDialog(g.dialog);
            resetStyles(g.dialog, g.list);
          }, 170);
        } else {
          snapBack(g);
        }
      }
    }

    // Passive on the document: these never hold up scrolling. The one listener that may cancel (touchmove) is added
    // to the fullscreen photo list in wire(), so it only covers that list, and only while the dialog is open.
    document.addEventListener('touchstart', onStart, { passive: true, capture: true });
    document.addEventListener('touchend', onEnd, { passive: true, capture: true });
    document.addEventListener('touchcancel', onEnd, { passive: true, capture: true });

    function wire(dialog) {
      if (dialog.__jhSwipe) return;
      dialog.__jhSwipe = true;
      var list = dialog.querySelector(LIST);
      if (!list) return;
      list.addEventListener('touchmove', onMove, { passive: false, capture: true }); // capture: runs before the zoom wrapper, as before
      var timer = 0;
      var atGlideEnd = function () {
        var glide = gliding.get(list);
        if (glide && Math.abs(list.scrollLeft - glide.to * list.clientWidth) < 2) finishGlide(dialog, list);
      };
      list.addEventListener('scroll', function () {
        updateCounter(dialog, list);
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          if (gesture) return;
          if (gliding.has(list)) atGlideEnd(); // never sync from a position the glide is passing through
          else syncTheme(dialog, list);
        }, 90);
      }, { passive: true });
      list.addEventListener('scrollend', atGlideEnd, { passive: true });
      counterFor(dialog);
      new MutationObserver(function () {
        if (!dialog.open) return;
        // the theme scrolls to the opened photo right after showModal(); read it once that has happened
        [0, 120, 400].forEach(function (ms) {
          window.setTimeout(function () {
            updateCounter(dialog, list);
            sync.set(list, indexOf(list));
          }, ms);
        });
      }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }

    function init() {
      document.querySelectorAll('.dialog-zoomed-gallery__dialog').forEach(wire);
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();

    window.addEventListener('dialog:close', function () {
      document.querySelectorAll('.dialog-zoomed-gallery__dialog').forEach(function (d) {
        var l = d.querySelector(LIST);
        if (l) {
          endGlide(l);
          resetStyles(d, l);
        }
      });
    });
  } catch (err) {
    /* fail silently: never block the page */
  }
})();
